import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { 
  ShoppingCart, 
  User, 
  Menu, 
  X, 
  Globe,
  Bell,
  ChevronDown
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const HambreLogo = () => (
  <svg viewBox="0 0 100 100" className="w-10 h-10">
    <rect width="100" height="100" rx="20" fill="#F97316"/>
    <g fill="white">
      {/* Fork */}
      <rect x="18" y="25" width="4" height="50" rx="2"/>
      <rect x="25" y="25" width="3" height="20" rx="1.5"/>
      <rect x="18" y="25" width="3" height="20" rx="1.5"/>
      <rect x="11" y="25" width="3" height="20" rx="1.5"/>
      {/* Spoon */}
      <ellipse cx="78" cy="35" rx="12" ry="15"/>
      <rect x="76" y="45" width="4" height="30" rx="2"/>
      {/* H connector */}
      <rect x="30" y="50" width="44" height="8" rx="4"/>
    </g>
  </svg>
);

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { getItemCount } = useCart();
  const { currency, setCurrency, getAvailableCurrencies } = useCurrency();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const itemCount = getItemCount();
  const currencies = getAvailableCurrencies();

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    // Don't set dir="rtl" on document - it breaks the entire layout
    // RTL is handled by CSS for specific text elements only
  };

  const languages = [
    { code: 'en', name: 'English', flag: '/flags/gb.svg' },
    { code: 'es', name: 'Español', flag: '/flags/es.svg' },
    { code: 'fr', name: 'Français', flag: '/flags/fr.svg' },
    { code: 'de', name: 'Deutsch', flag: '/flags/de.svg' },
    { code: 'nl', name: 'Nederlands', flag: '/flags/nl.svg' },
    { code: 'nl-BE', name: 'Nederlands (BE)', flag: '/flags/be.svg' },
    { code: 'fr-BE', name: 'Français (BE)', flag: '/flags/be.svg' },
    { code: 'hi', name: 'हिन्दी', flag: '/flags/in.svg' },
    { code: 'pa', name: 'ਪੰਜਾਬੀ', flag: '/flags/pa.svg' },
  ];

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  const getDashboardLink = () => {
    if (!user) return '/login';
    switch (user.role) {
      case 'super_admin': return '/admin';
      case 'restaurant_admin': return '/restaurant';
      case 'driver': return '/driver';
      case 'sales_person': return '/sales';
      default: return '/orders';
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <HambreLogo />
            <span className="text-xl font-bold text-slate-900 group-hover:text-orange-500 transition-colors">
              Hambre
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-colors ${location.pathname === '/' ? 'text-orange-500' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid="nav-home"
            >
              {t('nav.home')}
            </Link>
            <Link 
              to="/restaurants" 
              className={`text-sm font-medium transition-colors ${location.pathname === '/restaurants' ? 'text-orange-500' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid="nav-restaurants"
            >
              {t('nav.restaurants')}
            </Link>
            {user && (
              <Link 
                to="/orders" 
                className={`text-sm font-medium transition-colors ${location.pathname === '/orders' ? 'text-orange-500' : 'text-slate-600 hover:text-slate-900'}`}
                data-testid="nav-orders"
              >
                {t('nav.orders')}
              </Link>
            )}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Currency Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-slate-600 font-medium" data-testid="currency-selector">
                  {currencies.find(c => c.code === currency)?.symbol || '£'} {currency}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {currencies.map((curr) => (
                  <DropdownMenuItem 
                    key={curr.code}
                    onClick={() => setCurrency(curr.code)}
                    className={currency === curr.code ? 'bg-orange-50 text-orange-600' : ''}
                  >
                    <span className="w-6">{curr.symbol}</span>
                    <span>{curr.code} - {curr.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-slate-600 gap-1 px-2" data-testid="language-selector">
                  <img src={currentLang.flag} alt={currentLang.name} className="w-5 h-4 object-cover rounded-sm" />
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[160px]">
                {languages.map((lang) => (
                  <DropdownMenuItem 
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`flex items-center gap-3 ${i18n.language === lang.code ? 'bg-orange-50 text-orange-600' : ''}`}
                  >
                    <img src={lang.flag} alt={lang.name} className="w-5 h-4 object-cover rounded-sm" />
                    <span>{lang.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Cart */}
            <Link to="/cart" className="relative" data-testid="cart-link">
              <Button variant="ghost" size="icon" className="text-slate-600">
                <ShoppingCart className="h-5 w-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </Button>
            </Link>

            {/* Notifications */}
            {user && (
              <Link to="/notifications" className="relative" data-testid="notifications-link">
                <Button variant="ghost" size="icon" className="text-slate-600">
                  <Bell className="h-5 w-5" />
                </Button>
              </Link>
            )}

            {/* User Menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                      <User className="h-4 w-4 text-orange-600" />
                    </div>
                    <span className="hidden sm:block text-sm font-medium text-slate-700">{user.name}</span>
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardLink()}>Dashboard</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile">{t('nav.profile')}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} className="text-red-600">
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" data-testid="login-link">
                  <Button variant="ghost" className="text-sm font-medium">
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to="/register" data-testid="signup-link">
                  <Button className="btn-primary text-sm">
                    {t('nav.signup')}
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-16 left-0 right-0 bg-white border-b border-slate-200 shadow-lg animate-fade-in">
          <div className="px-4 py-4 space-y-2">
            <Link 
              to="/" 
              className="block px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.home')}
            </Link>
            <Link 
              to="/restaurants" 
              className="block px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.restaurants')}
            </Link>
            {user && (
              <Link 
                to="/orders" 
                className="block px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.orders')}
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
